import React, { useState } from "react";
import { Zap, X, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const COUNTER_LABELS = {
  sent: "sent",
  drafted: "drafted",
  voicemails: "voicemails",
  ai_calls: "AI calls",
  researched: "researched",
  skipped_weekend: "weekend-skipped",
  skipped_human_only: "human-only drafted",
  skipped_other: "left for human",
  errors: "errored",
};

export default function RunMyDayFAB() {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/runner/preview");
      setPreview(data);
      setOpen(true);
    } catch (err) {
      console.warn("[RunMyDayFAB] preview failed", err);
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const doRun = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/runner/run-now");
      setLastRun(data);
      setPreview(null);
      const c = data.counters || {};
      const summary = Object.entries(c).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${COUNTER_LABELS[k] || k}`).join(" · ") || "Nothing to do right now";
      toast.success(`Run my day · ${summary}`);
      setOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <button
        onClick={doPreview}
        disabled={previewing}
        data-testid="run-my-day-fab"
        title="Run my day"
        className="fixed bottom-24 right-6 z-50 h-14 w-14 bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-all flex items-center justify-center disabled:opacity-50 group"
        style={{ borderRadius: 999 }}
      >
        <Zap className={`h-5 w-5 ${previewing ? "animate-pulse" : "group-hover:rotate-12 transition-transform"}`} strokeWidth={2} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-end sm:justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-background border border-border w-full max-w-xl max-h-[85vh] overflow-y-auto"
            style={{ borderRadius: 2 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="run-my-day-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="run-my-day-title"
          >
            <div className="px-6 py-5 border-b border-border flex items-start justify-between sticky top-0 bg-background z-10">
              <div>
                <div className="overline mb-1">Run my day</div>
                <div className="font-serif text-2xl tracking-tight" id="run-my-day-title" style={{ fontWeight: 400 }}>
                  {lastRun ? "Daily run complete" : "Today's plan"}
                </div>
                {(preview || lastRun) && (
                  <div className="text-xs text-muted-foreground font-mono mt-1.5">
                    Mode <span className="text-foreground">{(lastRun || preview).mode}</span> · cap <span className="text-foreground">{(lastRun || preview).cap}</span>
                    {(lastRun || preview).weekend && <span className="text-foreground"> · weekend (LinkedIn-only)</span>}
                  </div>
                )}
              </div>
              <button onClick={() => setOpen(false)} data-testid="run-my-day-close" className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" strokeWidth={1.5} /></button>
            </div>

            <div className="p-6 space-y-4">
              {lastRun && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {Object.entries(lastRun.counters || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                      <div key={k} className="border border-border bg-card px-3 py-2" style={{ borderRadius: 2 }}>
                        <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>{v}</div>
                        <div className="overline mt-0.5">{COUNTER_LABELS[k] || k}</div>
                      </div>
                    ))}
                  </div>
                  {(lastRun.results || []).length === 0 && (
                    <div className="text-sm text-muted-foreground">Nothing actionable in your queue right now. Try scanning triggers or seeding more leads.</div>
                  )}
                  {(lastRun.results || []).map((r, i) => (
                    <div key={i} className="border border-border bg-card p-3" style={{ borderRadius: 2 }} data-testid={`run-result-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm">{r.headline}</div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1 font-mono">
                            {r.type} · {r.outcome || (r.executed) || (r.ok ? "ok" : "error")}
                          </div>
                          {r.error && <div className="text-xs text-destructive mt-1">{r.error}</div>}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {preview && !lastRun && (
                <>
                  {(preview.planned || []).length === 0 ? (
                    <div className="text-sm text-muted-foreground">Nothing planned right now. Try refreshing your coach actions or scanning triggers.</div>
                  ) : (
                    <div className="space-y-2">
                      {(preview.planned || []).map((p, i) => (
                        <div key={p.action_id} className="border border-border bg-card p-3 flex items-start gap-3" style={{ borderRadius: 2 }} data-testid={`run-preview-${i}`}>
                          <div className="text-xl font-serif tracking-tight text-muted-foreground/40 w-6 shrink-0 leading-none mt-0.5">{i + 1}</div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm">{p.headline}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1 font-mono">
                              {p.type} · {p.decision === "execute" ? "auto-execute" : p.decision === "skipped_weekend" ? "skipped (weekend)" : "draft only"}
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" strokeWidth={1.5} />
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 pt-3 border-t border-border">
                    <Button onClick={doRun} disabled={running || (preview.planned || []).length === 0} data-testid="run-my-day-confirm" style={{ borderRadius: 2 }} className="flex-1">
                      <Zap className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> {running ? "Running…" : `Run ${(preview.planned || []).length} actions`}
                    </Button>
                    <Button variant="outline" onClick={() => setOpen(false)} style={{ borderRadius: 2 }}>Cancel</Button>
                  </div>
                </>
              )}

              <div className="pt-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Schedule auto-runs in <Link to="/settings" className="underline hover:text-foreground">Settings → Run my day</Link>.</span>
                {lastRun && (
                  <button className="underline hover:text-foreground" onClick={() => { setLastRun(null); doPreview(); }} data-testid="run-my-day-replan">Plan another run</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
